import { useMemo, useState } from 'react';
import { useDebounce } from 'common/useDebounce';
import { useTranslation } from 'react-i18next';
import { SetStateAction, useAtom, WritableAtom } from 'jotai';
import { useAccountsAutocomplete } from './hooks/useAccountsAutocomplete';
import { Autocomplete } from 'common/components/Inputs/Autocomplete/Autocomplete';

const MAX_ACCOUNTS_TO_FILTER = 10;

type AccountSearchFilterProps = {
  accountsFilterAtom: WritableAtom<(string | null)[], [SetStateAction<(string | null)[]>], void>;
  accountsHintsAtom: WritableAtom<string[], [any], void>;
};

export const AccountSearchFilter = ({
  accountsFilterAtom,
  accountsHintsAtom,
}: AccountSearchFilterProps) => {
  const { t } = useTranslation();

  const [hints, removeHints] = useAtom(accountsHintsAtom);

  const [accountSearch, setAccountSearch] = useState('');
  const debounceAccount = useDebounce(accountSearch, 500);
  const blankValuesEnabled = !debounceAccount.length;

  const [accounts, setAccounts] = useAtom(accountsFilterAtom);

  const {
    options: accountsOptions,
    isSelectedOptionFetching,
    isFetched,
    selectedOptions,
    hintOptions,
    updateSelectedOptions,
    hasNextPage,
    fetchNextPage,
  } = useAccountsAutocomplete({
    search: debounceAccount,
    initialIds: accounts,
    hints: hints,
    onSelectedChange: (options) => {
      setAccounts(options.map(({ id }) => id));
    },
  });

  const hasMaxAccountLimitsOccurs = accounts && accounts.length >= MAX_ACCOUNTS_TO_FILTER;
  const errors = useMemo(() => {
    let errors: string[] = [];
    if (hasMaxAccountLimitsOccurs) {
      errors = [t('common.limitReached')];
    }

    return errors;
  }, [hasMaxAccountLimitsOccurs]);

  return (
    <Autocomplete
      data-testid="accounts-multiselect"
      placeholder={t('rSimInventory.accountFilterPlaceholder')}
      showNameAndId
      skeletonRepeats={accounts.length}
      inputLoading={isSelectedOptionFetching}
      options={accountsOptions}
      value={selectedOptions}
      hints={hintOptions}
      inputValue={accountSearch}
      onChange={updateSelectedOptions}
      onSearch={setAccountSearch}
      loading={!isFetched}
      onHintRemove={removeHints}
      errors={errors}
      disallowSelecting={hasMaxAccountLimitsOccurs}
      disallowSelectingReason={t('common.limitReached')}
      blankValueEnabled={blankValuesEnabled}
      hasMore={hasNextPage}
      onMoreShouldBeLoad={fetchNextPage}
    />
  );
};
